/* ==========================================================================
   TGM product page behaviour (2026-10-02)
   1. Accessible dialogs shared by the gift card and the option guides.
   2. Size spec chip and size detail row that follow the selected variant.
   3. Spec chips kept on one line (03-10-2026).
   4. "Most popular" variant preselected on load (03-10-2026).
   Deferred, no dependencies, safe to load on any template.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------------------------------------------------------------- dialogs */

  var openDialog = null;
  var lastFocused = null;

  function focusables(dialog) {
    return Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  function close() {
    if (!openDialog) return;
    openDialog.setAttribute('hidden', '');
    document.documentElement.classList.remove('tgm-dialog-open');
    openDialog = null;
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function open(dialog, trigger) {
    if (!dialog) return;
    if (openDialog) close();
    lastFocused = trigger || document.activeElement;
    dialog.removeAttribute('hidden');
    document.documentElement.classList.add('tgm-dialog-open');
    openDialog = dialog;
    var first = focusables(dialog)[0];
    if (first) first.focus();
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-tgm-dialog-open]');
    if (opener) {
      var dialog = document.getElementById(opener.getAttribute('data-tgm-dialog-open'));
      if (dialog) {
        event.preventDefault();
        open(dialog, opener);
      }
      return;
    }
    if (event.target.closest('[data-tgm-dialog-close]')) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!openDialog) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables(openDialog);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------- size chip and size row */

  function selectedValueFor(scope, optionName) {
    if (!optionName) return '';
    var radios = scope.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) {
      var name = radios[i].getAttribute('name') || '';
      if (name === optionName || name === 'options[' + optionName + ']') {
        return (radios[i].value || '').trim();
      }
    }
    var selects = scope.querySelectorAll('select[name]');
    for (var j = 0; j < selects.length; j++) {
      var selectName = selects[j].getAttribute('name') || '';
      if (selectName === 'options[' + optionName + ']' || selectName === optionName) {
        return (selects[j].value || '').trim();
      }
    }
    return '';
  }

  function formatSize(target, value) {
    if (target.getAttribute('data-tgm-chip-spaced') !== 'true') return value;
    return value.replace(/\s*mm\b/gi, ' mm');
  }

  function syncSizes() {
    var targets = document.querySelectorAll('[data-tgm-chip-size]');
    for (var i = 0; i < targets.length; i++) {
      var target = targets[i];
      var scope = target.closest('.shopify-section') || document;
      var optionName = target.getAttribute('data-tgm-chip-option');
      var initial = target.getAttribute('data-tgm-chip-all') || '';
      var slot = target.querySelector('.tgm-chip__value');
      if (!slot) continue;
      var selected = selectedValueFor(scope, optionName);
      var next = selected ? formatSize(target, selected) : initial;
      if (next && slot.textContent.trim() !== next) slot.textContent = next;
    }
    fitChips();
  }

  document.addEventListener('change', function (event) {
    if (event.target.matches('input[type="radio"], select')) syncSizes();
  });

  /* ------------------------------------------------- chips on one line */

  // The row never wraps: the text shrinks by half pixels down to 10.5px when it does not fit,
  // and only if it still does not fit the row scrolls sideways (scrollbar hidden).
  var MIN_CHIP_PX = 10.5;

  function fitChips() {
    var rows = document.querySelectorAll('[data-tgm-chips]');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      row.classList.remove('is-scroll');
      row.style.removeProperty('--tgm-chip-fs-fit');
      if (row.scrollWidth <= row.clientWidth + 1) continue;
      var size = parseFloat(getComputedStyle(row.querySelector('.tgm-chip') || row).fontSize) || 13;
      while (size > MIN_CHIP_PX && row.scrollWidth > row.clientWidth + 1) {
        size -= 0.5;
        row.style.setProperty('--tgm-chip-fs-fit', size + 'px');
      }
      if (row.scrollWidth > row.clientWidth + 1) row.classList.add('is-scroll');
    }
  }

  var fitTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(fitChips, 120);
  });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitChips);

  /* ------------------------------------------------- "Most popular" preselection */

  // The variant comes from Liquid (snippets/tgm-popular-variant.liquid): it is only printed when
  // the URL has no ?variant=, the badged combination exists and is in stock, and the template is
  // not a configurator. Here the matching inputs are checked and the theme's own change handler
  // runs, so price, compare price, image, availability, Add to cart, sticky bar and URL all
  // update through the same code path as a customer click.
  function preselectPopular() {
    if (/[?&]variant=/.test(window.location.search)) return;
    var picker = document.querySelector('variant-selects[data-tgm-popular-variant], variant-radios[data-tgm-popular-variant]');
    if (!picker || picker.dataset.tgmPreselected) return;
    picker.dataset.tgmPreselected = '1';
    var id = Number(picker.getAttribute('data-tgm-popular-variant'));
    var json = picker.querySelector('script[type="application/json"]');
    if (!id || !json) return;
    var variants;
    try { variants = JSON.parse(json.textContent); } catch (e) { return; }
    var variant = variants.filter(function (v) { return v.id === id; })[0];
    if (!variant || !variant.available) return;

    // Option groups in DOM order: radios grouped by name, or one select per option.
    var groups = [];
    var seen = {};
    picker.querySelectorAll('input[type="radio"], select').forEach(function (el) {
      var key = el.tagName === 'SELECT' ? 'select:' + groups.length : el.name;
      if (seen[key]) return;
      seen[key] = true;
      groups.push(el.tagName === 'SELECT' ? [el] : picker.querySelectorAll('input[type="radio"][name="' + CSS.escape(el.name) + '"]'));
    });

    var changed = null;
    variant.options.forEach(function (value, index) {
      var group = groups[index];
      if (!group) return;
      if (group[0] && group[0].tagName === 'SELECT') {
        if (group[0].value !== value) { group[0].value = value; changed = group[0]; }
        return;
      }
      Array.prototype.forEach.call(group, function (input) {
        if (input.value === value && !input.checked) { input.checked = true; changed = input; }
      });
    });
    if (!changed) return;

    // The theme may attach its change listener a little after the element is defined, so the
    // event is fired again until the variant id of the product form really is the popular one
    // (up to ~4 s), then it stops. Nothing else changes if it never takes.
    var sectionId = picker.getAttribute('data-section') || '';
    var idInput = function () {
      return document.querySelector('#product-form-' + sectionId + ' input[name="id"]') ||
        document.querySelector('form[id*="' + sectionId + '"] input[name="id"]') ||
        document.querySelector('input.product-variant-id');
    };
    var tries = 0;
    var fire = function () {
      var input = idInput();
      if (input && String(input.value) === String(id)) return;
      var gallery = document.querySelector('media-gallery');
      if (gallery && typeof gallery.setActiveMedia !== 'function' && tries < 16) { tries++; setTimeout(fire, 250); return; }
      if (tries++ > 16) return;
      changed.dispatchEvent(new Event('change', { bubbles: true }));
      setTimeout(fire, 250);
    };
    fire();
  }

  function whenPickerReady(fn) {
    var run = function () {
      // The theme's variant change also moves the gallery (media-gallery.setActiveMedia), so the
      // gallery element has to be defined too before the preselection fires.
      var defs = ['variant-selects', 'variant-radios', 'media-gallery'].filter(function (n) { return document.querySelector(n); });
      if (!defs.length || !window.customElements) return fn();
      Promise.race([
        Promise.all(defs.map(function (n) { return customElements.whenDefined(n); })),
        new Promise(function (r) { setTimeout(r, 3000); })
      ]).then(fn);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
  }

  whenPickerReady(function () {
    preselectPopular();
    fitChips();
  });
})();
